'use client';
import type {
  BlockSlug,
  ContentBlock as ContentBlockProps,
  Page,
  ReusableContentBlock as ReusableContentBlockProps
} from '@codeware/shared/util/payload-types';
import type { BlocksData } from '@codeware/shared/util/payload-utils';
import { cn } from '@codeware/shared/util/ui';
import { useRef } from 'react';

import { AboutBlock } from './blocks/about/AboutBlock';
import { BlockGalleryBlock } from './blocks/block-gallery/BlockGalleryBlock';
import { CalloutBlock } from './blocks/callout/CalloutBlock';
import { CardBlock } from './blocks/card/CardBlock';
import { CodeBlock } from './blocks/code/CodeBlock';
import { CustomComponentBlock } from './blocks/custom-component/CustomComponentBlock';
import { FeatureCardsBlock } from './blocks/feature-cards/FeatureCardsBlock';
import { FeatureSectionBlock } from './blocks/feature-section/FeatureSectionBlock';
import { FileAreaBlock } from './blocks/file-area/FileAreaBlock';
import { FormBlock } from './blocks/form/FormBlock';
import { HeroBlock } from './blocks/hero/HeroBlock';
import { ImageBlock } from './blocks/image/ImageBlock';
import { PillListBlock } from './blocks/pill-list/PillListBlock';
import { PostsBlock } from './blocks/posts/PostsBlock';
import { RichText } from './blocks/RichText';
import { ShowcaseBlock } from './blocks/showcase/ShowcaseBlock';
import { SocialMediaBlock } from './blocks/social-media/SocialMediaBlock';
import { SpacingBlock } from './blocks/spacing/SpacingBlock';
import { TestimonialBlock } from './blocks/testimonial/TestimonialBlock';
import { ThemeStudioBlock } from './blocks/theme-studio/ThemeStudioBlock';
import { ToursBlock } from './blocks/tours/ToursBlock';
import { Band, type SectionBand } from './layout/Band';
import { ContainerInner } from './layout/Container';
import { ColumnSizeProvider } from './providers/ColumnSizeProvider';

type ContentBlockWithData = ContentBlockProps & {
  blocksData?: BlocksData;
  preview?: boolean;
};
type ReusableContentBlockWithData = ReusableContentBlockProps & {
  blocksData?: BlocksData;
  preview?: boolean;
  framed?: boolean;
};

/**
 * Render Payload content block data, with the configured number of columns
 * and rich text content in each column.
 *
 * Optional blocks are rendered after the rich text content, within each column.
 *
 * User defined column sizes are applied for tablets and desktops.
 * Mobile devices display all columns as full width.
 */
export const ContentBlock: React.FC<ContentBlockWithData> = ({
  columns,
  blocksData,
  preview
}) => {
  return (
    // Clipped sideways only, and a little outside its own edge, so a ring or
    // shadow on a block at the edge of a column is not cut off
    <div className="-mx-1 grid w-[calc(100%+0.5rem)] grid-cols-12 gap-x-4 gap-y-8 overflow-x-clip px-1 md:gap-x-8 lg:gap-x-16">
      {columns?.map((col, index) => {
        const { blocks, richText } = col;
        const size = col.size ?? 'full';

        return (
          <ColumnSizeProvider size={size} key={index}>
            <div
              className={cn('first-child-no-margin col-span-12', {
                'md:col-span-4': size === 'one-third',
                'md:col-span-6': size === 'half',
                'md:col-span-8': size === 'two-thirds'
              })}
            >
              {richText && <RichText data={richText} />}
              {!!blocks?.length && (
                <RenderBlocks
                  className={cn({ 'mt-8': !!richText })}
                  blocks={blocks}
                  blocksData={blocksData}
                  preview={preview}
                />
              )}
            </div>
          </ColumnSizeProvider>
        );
      })}
    </div>
  );
};

/**
 * Render Payload reusable content block data, by rendering its layout blocks.
 */
export const ReusableContentBlock: React.FC<ReusableContentBlockWithData> = ({
  reusableContent,
  refId,
  blocksData,
  preview,
  framed
}) => {
  if (reusableContent && typeof reusableContent === 'object') {
    return (
      <RenderBlocks
        blocks={reusableContent.layout}
        refId={refId}
        blocksData={blocksData}
        preview={preview}
        framed={framed}
        nested
      />
    );
  }
  return null;
};

// Why are the components above defined here and not in `blocks/` folder?
// Components depend on `RenderBlocks`. To avoid circular dependencies,
// these components must be defined in the same file as `RenderBlocks`.

/**
 * Resolve extra props to inject into a block component from pre-fetched data.
 *
 * Blocks that use Payload relationship fields get their data resolved automatically
 * by Payload's `depth` option and need no extra props.
 *
 * Blocks that query a collection dynamically (listing blocks) receive pre-fetched
 * data keyed by block id via `blocksData`. This function maps each such block type
 * to its resolved data, or threads `blocksData` through container blocks so nested
 * listing blocks can also receive their data.
 *
 * When adding a new listing block, add a case here alongside its entry in
 * `BlocksData`.
 */
function resolveBlockProps(
  block: NonNullable<Page['layout']>[number],
  blocksData: BlocksData | undefined,
  preview: boolean | undefined,
  framed: boolean | undefined
): Record<string, unknown> {
  switch (block.blockType) {
    // Container blocks: thread blocksData through so nested listing blocks receive their data
    case 'content':
      return { blocksData, preview };
    // Its blocks stand in for it on the page, so they are framed as it would be
    case 'reusable-content':
      return { blocksData, preview, framed };

    // Drawn as an example, where the form document belongs to nobody: the
    // fields are real, and submitting would post to a form that does not
    // exist, so the button says so by being disabled rather than by failing
    case 'form':
      return preview ? { disabled: true } : {};

    // The gallery draws its examples with the renderer rather than reaching
    // into `blocksMap`, which would import this module and close a cycle
    case 'block-gallery':
      return { render: RenderBlocks };

    // Listing blocks: resolve pre-fetched collection data by block id
    case 'posts':
      return { posts: (block.id && blocksData?.posts?.[block.id]) ?? [] };
    case 'tours':
      return { tours: (block.id && blocksData?.tours?.[block.id]) ?? [] };

    default:
      return {};
  }
}

/** Block slug to component mapping */
const blocksMap: Record<
  BlockSlug,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.JSXElementConstructor<any>
> = {
  about: AboutBlock,
  'block-gallery': BlockGalleryBlock,
  callout: CalloutBlock,
  card: CardBlock,
  code: CodeBlock,
  content: ContentBlock,
  'custom-component': CustomComponentBlock,
  'feature-cards': FeatureCardsBlock,
  'feature-section': FeatureSectionBlock,
  'file-area': FileAreaBlock,
  form: FormBlock,
  hero: HeroBlock,
  image: ImageBlock,
  'pill-list': PillListBlock,
  posts: PostsBlock,
  'reusable-content': ReusableContentBlock,
  showcase: ShowcaseBlock,
  'social-media': SocialMediaBlock,
  spacing: SpacingBlock,
  testimonial: TestimonialBlock,
  'theme-studio': ThemeStudioBlock,
  tours: ToursBlock
};

type Props = {
  blocks: Page['layout'];
  blocksData?: BlocksData;
  refId?: string | null | undefined;
  className?: string;
  /**
   * Drawn as an example rather than as the page itself.
   *
   * The block gallery renders real blocks with data nobody owns, so anything
   * that would reach for a document that is not there is told to stand still.
   */
  preview?: boolean;
  /**
   * The page's own blocks, drawn inside the container's outer layer.
   *
   * Each block brings the inner layer itself, so its band can sit between the
   * two and span the sheet. Blocks nested in a column, or drawn in the
   * gallery, have no sheet to span and keep their band to themselves.
   */
  framed?: boolean;
  /**
   * The blocks of a reusable content block, drawn inside the page's own.
   *
   * Only the page's own list can say it ends on a band: a nested list ending
   * on one may sit anywhere on the page.
   */
  nested?: boolean;
};

/** A block's band, `none` for a block that has none or cannot have one */
const bandOf = (block: Props['blocks'][number]): SectionBand =>
  ('band' in block ? block.band : null) ?? 'none';

/**
 * Renders the blocks of a page.
 *
 * @throws Error if not wrapped in PayloadProvider (development only)
 */
export const RenderBlocks: React.FC<Props> = ({
  blocks,
  blocksData,
  className,
  framed,
  nested,
  preview,
  refId
}) => {
  const docRef = useRef<HTMLDivElement>(null);

  const hasBlocks = blocks && Array.isArray(blocks) && blocks.length > 0;

  if (hasBlocks) {
    return (
      <div
        id={refId ?? undefined}
        ref={docRef}
        className={className}
        // A page that ends on a band spanning it meets the footer directly,
        // which the layout reads from here
        data-ends-on-band={
          framed && !nested && bandOf(blocks[blocks.length - 1]) !== 'none'
            ? ''
            : undefined
        }
      >
        {blocks.map((block, index) => {
          const Block = blocksMap[block.blockType];

          // Spacing block or the first block after spacing block
          const isSpacingBlockOrFollowing =
            block.blockType === 'spacing' ||
            (index > 0 && blocks[index - 1].blockType === 'spacing');

          if (Block) {
            const extraProps = resolveBlockProps(
              block,
              blocksData,
              preview,
              framed
            );

            const band = bandOf(block);

            // Two bands spanning the page meet edge to edge: each carries its
            // own padding, and the gap between them read as a stray strip.
            // Contained bands are rounded panels and keep their distance
            const followsBand =
              framed &&
              index > 0 &&
              band !== 'none' &&
              bandOf(blocks[index - 1]) !== 'none';

            // Do not set any margins around spacing block since it has its own margin options.
            // For all other blocks, set a top margin unless it's the first block.
            const spacing = cn({
              'not-first:mt-16 md:not-first:mt-24':
                !isSpacingBlockOrFollowing && !followsBand
            });

            // Its own blocks are framed one by one, so it takes no frame here
            if (framed && block.blockType === 'reusable-content') {
              return (
                <div className={spacing} key={index}>
                  <Block {...block} {...extraProps} />
                </div>
              );
            }

            if (framed) {
              return (
                <Band band={band} fit="sheet" className={spacing} key={index}>
                  <ContainerInner>
                    <Block {...block} {...extraProps} />
                  </ContainerInner>
                </Band>
              );
            }

            return (
              <Band band={band} fit="contained" className={spacing} key={index}>
                <Block {...block} {...extraProps} />
              </Band>
            );
          }
          return null;
        })}
      </div>
    );
  }

  return null;
};
